import {Injectable} from '@angular/core';
import {AuthService} from './auth.service';
import {PermissionService} from './permission.service';
import {FeatureToggleService} from '../shared/feature-toggle/feature-toggle.service';
import {TokenService} from './token.service';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {UserData} from '../shared/common-components/lms-header/service/lms-header-common.model';
import {GlobalConstants} from '../shared/global-constants';
import {environment} from '../../environments/environment';
import {LmsHeaderCommonService} from '../shared/common-components/lms-header/service/lms-header-common.service';
import {DashboardDataService} from '../dashboard/dashboard-data.service';
import {CommonService} from '../shared/common.service';

@Injectable({
  providedIn: 'root'
})
export class AppInitializerService {

  constructor(
    private readonly authService: AuthService,
    private readonly permissionService: PermissionService,
    private readonly featureToggleService: FeatureToggleService,
    private readonly tokenService: TokenService,
    private readonly lmsHeaderCommonService: LmsHeaderCommonService,
    private readonly dashboardDataService: DashboardDataService,
    private readonly commonService: CommonService,
  ) {}

  initConfig(): Observable<any> {
    return this.tokenService.waitUntilLoaded$
      .pipe(
        switchMap((loaded: boolean): Observable<any> => {
          if (loaded) {
            console.log('[APP_INIT] Token loaded, initializing authenticated data');
            return this.loadAuthenticatedData();
          }
          console.log('[APP_INIT] No token, initializing public data only');
          return this.loadPublicData();
        }),
      );
  }

  private readonly loadPublicData = () => {
    return this.featureToggleService.getEnabledFeatures().pipe(
      tap(() => console.log('[APP_INIT] Public feature toggles loaded')),
      catchError(err => {
        console.error('[APP_INIT] Failed to load public data:', err);
        return of([]);
      })
    );
  }

  private readonly loadAuthenticatedData = () => forkJoin([
    this.permissionService.getUserPermissions().pipe(
      tap(() => console.log('[APP_INIT] User Permissions loaded')),
      catchError(err => {
        console.error('[APP_INIT] Failed to load user permissions:', err);
        // allow bootstrap to continue
        return of([]);
      })
    ),
    this.featureToggleService.getEnabledFeatures().pipe(
      tap(() => console.log('[APP_INIT] Feature toggles loaded')),
      catchError(err => {
        console.error('[APP_INIT] Failed to load feature toggles:', err);
        // allow bootstrap to continue
        return of([]);
      })
    ),
    this.lmsHeaderCommonService.getLoggedUser().pipe(
      tap((userData: UserData) => {
        localStorage.setItem(GlobalConstants.REDIRECT_URL, environment.redirectUrl);
        localStorage.setItem(GlobalConstants.ASSIGNED_OU_LIST, userData.encryptedAssignedOUList);
        console.log('[APP_INIT] Get logged user data loaded');
      }),
      catchError(err => {
        console.error('[APP_INIT] Failed to load logged user data:', err);
        // allow bootstrap to continue
        return of([]);
      })
    ),
    this.dashboardDataService.getConfig().pipe(
      tap((res) => {
        this.dashboardDataService.setConfig(res);
        localStorage.setItem(
          GlobalConstants.LMS_CONFIG,
          this.commonService.encrypt(JSON.stringify(res))
        );
        console.log('[APP_INIT] Config loaded');
      }),
      catchError(err => {
        console.error('[APP_INIT] Failed to load config:', err);
        // allow bootstrap to continue
        return of({});
      })
    ),
  ])
}
